import appConfig from 'app-config';
import HomeClient from 'flow/api-clients/home-client';

import Enum from 'enum';
import { getBrand } from 'flow/brands/brands';
import { List, Map, Set } from 'immutable';
import moment from 'moment';

import { findExtension } from 'flow/utils/extension-utils';
import { getStandardProfileDescriptionInUserLanguage } from 'flow/utils/profile-utils';

import { sortStrings } from 'flow/utils/sorter-utils';
import DEFAULT_AVATAR_SVG from 'images/ic_telavox_default_avatar_60.svg';
import UNKNOWN_AVATAR_SVG from 'images/ic_telavox_unknown-contact.svg';
import { addUserPhoneLogEntry } from './browser-utils';
import { LocalesData } from './locales-data';

export const HTTPStatusCodes = Object.freeze({
    FORBIDDEN_403: 403,
    FORBIDDEN_401: 401,
    NOT_FOUND_404: 404,
});

const createExtensionStatus = (key, getColorFunc) => {
    return {
        key: key,
        getColor: getColorFunc,
    };
};

export const extensionStatuses = {
    available: createExtensionStatus(
        'available',
        () => getBrand().theme.palette.flavors.green,
    ),
    unavailable: createExtensionStatus(
        'unavailable',
        () => getBrand().theme.palette.greyscale.greyLight,
    ),
    inCall: createExtensionStatus(
        'inCall',
        () => getBrand().theme.palette.flavors.red,
    ),
    inExternalCall: createExtensionStatus(
        'inExternalCall',
        () => getBrand().theme.palette.flavors.red,
    ),
    ringingCall: createExtensionStatus(
        'ringingCall',
        () => getBrand().theme.palette.flavors.yellow,
    ),
    unknown: createExtensionStatus(
        'unknown',
        () => getBrand().theme.palette.flavors.flavor8,
    ),
};

export const isExtensionStatusInCall = (status) => {
    return (
        extensionStatuses.inCall.key === status ||
        extensionStatuses.inExternalCall.key === status
    );
};

export const StrategyState = new Enum({
    roundrobin: 'Roundrobin',
    sequential: 'Sequential',
    ringall: 'RingAll',
    leastrecent: 'Leastrecent',
    fewestcalls: 'FewestCalls',
    random: 'Random',
});

export const getTelavoxFormattedLanguageCode = (languageCode) => {
    if (!languageCode || typeof languageCode !== 'string') {
        return null;
    }

    const localesDataKeys = Object.keys(LocalesData);
    const languageCodeWithoutCountryCode = languageCode.split('-')[0];

    if (localesDataKeys.includes(languageCode)) {
        return languageCode;
    } else if (localesDataKeys.includes(languageCodeWithoutCountryCode)) {
        return languageCodeWithoutCountryCode;
    } else return null;
};

export const TRIGGER_TYPE = {
    ON_USER_INTERATION: 'onuserinteraction',
    ON_ANSWER: 'onanswer',
};

export const TriggerTypes = new Enum([
    TRIGGER_TYPE.ON_USER_INTERATION,
    TRIGGER_TYPE.ON_ANSWER,
]);

export const ScreenPositions = new Enum({
    TOP_LEFT: 'topLeft',
    TOP_MIDDLE: 'topMiddle',
    TOP_RIGHT: 'topRight',
    BOTTOM_LEFT: 'bottomleft',
    BOTTOM_RIGHT: 'bottomRight',
});

export const bg_flavor_colors = [
    'bg-flavor-5',
    'bg-flavor-9',
    'bg-flavor-2',
    'bg-flavor-1',
    'bg-flavor-3',
    'bg-flavor-7',
    'bg-flavor-8',
    'bg-flavor-6',
    'bg-flavor-4',
];

/**
 * Check if the device is a mobile
 */
export const isMobile = () => typeof window.orientation !== 'undefined';

/**
 * Extract a name from a ContactDTO
 */
export const extractName = (contact, defaultName = '') => {
    if (contact) {
        const first = contact.get('firstName');
        const last = contact.get('lastName');
        const fixed = contact.get('fixed');
        const mobile = contact.get('mobile');
        const company = contact.get('companyName');

        if (first && last) {
            return first + ' ' + last;
        } else if (first) {
            return first;
        } else if (last) {
            return last;
        } else if (company) {
            return company;
        } else if (fixed && fixed.get('type') !== 'not_a_number') {
            return fixed.get('nationalFormat');
        } else if (mobile && mobile.get('type') !== 'not_a_number') {
            return mobile.get('nationalFormat');
        } else {
            return defaultName;
        }
    }

    return defaultName;
};

export const extractFirstName = (contact, defaultName = '') => {
    if (contact) {
        const first = contact.get('firstName');
        const last = contact.get('lastName');

        if (first) {
            return first;
        } else if (last) {
            return last;
        } else {
            return defaultName;
        }
    }
    return defaultName;
};

export const extractNumberOrEmail = (thirdPartyContact) => {
    if (thirdPartyContact) {
        const number = thirdPartyContact.getIn(['phoneNumber', 'number']);
        const email = thirdPartyContact.get('email');

        if (number) {
            return number;
        } else if (email) {
            return email;
        }
    }

    return null;
};

export const extractAbbreviatedName = (contact, defaultName) => {
    if (contact) {
        const first = contact.get('firstName');
        const last = contact.get('lastName');

        if (first && !last) {
            return first;
        }
        if (!first && last) {
            return last;
        }
        if (first && last) {
            return first + ' ' + last[0];
        }
    }
    return defaultName;
};

export const getFormattedTime = (t, i18next) => {
    if (!t) {
        return '-';
    }
    const time = moment(t);
    const startOfDay = moment(t).startOf('day');
    const today = moment();

    const isToday = startOfDay.diff(today.startOf('day'), 'days') === 0;
    const isWithinWeek = startOfDay.diff(today.startOf('day'), 'days') > -7;
    const isWithinYear = startOfDay.diff(today.startOf('day'), 'years') === 0;

    if (isToday) {
        return time.format('HH:mm');
    } else if (isWithinWeek) {
        return time.locale(i18next.language).format('ddd');
    } else if (isWithinYear) {
        return time.locale(i18next.language).format('D MMM');
    } else {
        return time.locale(i18next.language).format('D MMM YYYY');
    }
};

export const getFormattedDateAndTime = (dateString, i18next) => {
    if (!dateString) {
        return '-';
    }
    const time = moment(dateString);
    const startOfDay = moment(dateString).startOf('day');
    const today = moment();

    const isToday = startOfDay.diff(today.startOf('day'), 'days') === 0;
    const isYesterday = startOfDay.diff(today.startOf('day'), 'days') === -1;

    if (isToday) {
        return time.format('HH:mm');
    } else if (isYesterday) {
        return i18next.t('yesterday');
    } else {
        return time.locale(i18next.language).format('D MMM');
    }
};

export const getDateDescription = (
    date,
    i18next,
    includeHoursMinutes = false,
) => {
    if (date === undefined) {
        return '';
    }
    const time = moment(date);
    const startOfDay = moment(date).startOf('day');
    const today = moment();

    const isToday = startOfDay.diff(today.startOf('day'), 'days') === 0;
    const isYesterday = startOfDay.diff(today.startOf('day'), 'days') === -1;
    const isWithinWeek = startOfDay.diff(today.startOf('day'), 'days') > -7;
    const isWithinYear = startOfDay.diff(today.startOf('day'), 'years') === 0;

    let dateString = '';
    if (isToday) {
        dateString = i18next.t('today');
    } else if (isYesterday) {
        dateString = i18next.t('yesterday');
    } else if (isWithinWeek) {
        dateString = time.locale(i18next.language).format('dddd');
    } else if (isWithinYear) {
        dateString = time.locale(i18next.language).format('D MMMM');
    } else {
        dateString = time.locale(i18next.language).format('D MMMM YYYY');
    }

    const hoursMinutesString = time.format('HH:mm');
    return includeHoursMinutes
        ? `${dateString} ${hoursMinutesString}`
        : dateString;
};

/**
 * Retrieve initials from name of supplied ContactDTO
 */
export const extractInitials = (contact, defaultValue = '?') => {
    if (!contact) {
        return defaultValue;
    }
    // Trimmed, so a name that is blank or starts with a space does not give a blank initial.
    const firstName = (contact.get('firstName') || '').trim();
    const lastName = (contact.get('lastName') || '').trim();
    const companyName = (contact.get('companyName') || '').trim();

    if (firstName && !lastName) {
        return firstName.substring(0, 1).toUpperCase();
    } else if (!firstName && lastName) {
        return lastName.substring(0, 1).toUpperCase();
    } else if (firstName && lastName) {
        return (
            firstName.substring(0, 1).toUpperCase() +
            lastName.substring(0, 1).toUpperCase()
        );
    } else if (companyName) {
        return companyName.substring(0, 1).toUpperCase();
    } else {
        return defaultValue;
    }
};

export const parseIfJSON = (data) => {
    let result;
    if (typeof data === 'string' && data.length > 0) {
        try {
            result = JSON.parse(data);
        } catch (_e) {
            result = data;
        }
    } else {
        result = data;
    }
    return result;
};

/**
 * Extracts a color from a provided key.
 *
 * @param key Any DTO-key with the format "NAME"-"KEYVALUE"
 * @returns {string}
 */
export const getColorFromKey = (key) => {
    const colors = [
        getBrand().theme.palette.flavors.flavor5,
        getBrand().theme.palette.flavors.flavor9,
        getBrand().theme.palette.flavors.flavor2,
        getBrand().theme.palette.flavors.flavor1,
        getBrand().theme.palette.flavors.flavor3,
        getBrand().theme.palette.flavors.flavor7,
        getBrand().theme.palette.flavors.flavor8,
        getBrand().theme.palette.flavors.flavor6,
        getBrand().theme.palette.flavors.flavor4,
    ];

    if (!key || key.indexOf('-') === -1) {
        // console.warn('Missing key in utils method');
        return colors[0];
    }

    const id = getIdFromEntityKey(key); // This ruins opaque IDs ;-(

    return colors[Math.abs(parseInt(id)) % colors.length];
};

export const getBgColorFromKey_TW = (key) => {
    const colors = bg_flavor_colors;

    if (!key || key.indexOf('-') === -1) {
        // console.warn('Missing key in utils method');
        return colors[0];
    }

    const id = getIdFromEntityKey(key); // This ruins opaque IDs ;-(

    return colors[Math.abs(parseInt(id)) % colors.length];
};

export const getBgColorFromContact_TW = (contact) => {
    const colors = bg_flavor_colors;

    if (!contact) {
        return colors[0];
    }

    const name = extractName(contact); // This ruins opaque IDs ;-(

    return colors[Math.abs(parseInt(name.length)) % colors.length];
};

export const getColorFromContact = (contact) => {
    const colors = [
        getBrand().theme.palette.flavors.flavor5,
        getBrand().theme.palette.flavors.flavor9,
        getBrand().theme.palette.flavors.flavor2,
        getBrand().theme.palette.flavors.flavor1,
        getBrand().theme.palette.flavors.flavor3,
        getBrand().theme.palette.flavors.flavor7,
        getBrand().theme.palette.flavors.flavor8,
        getBrand().theme.palette.flavors.flavor6,
        getBrand().theme.palette.flavors.flavor4,
    ];

    if (!contact) {
        console.warn('Missing key in utils method');
        return colors[0];
    }

    const name = extractName(contact); // This ruins opaque IDs ;-(

    return colors[Math.abs(parseInt(name.length)) % colors.length];
};

export const getColorFromInt = (hash) => {
    const colors = [
        getBrand().theme.palette.flavors.flavor5,
        getBrand().theme.palette.flavors.flavor9,
        getBrand().theme.palette.flavors.flavor2,
        getBrand().theme.palette.flavors.flavor1,
        getBrand().theme.palette.flavors.flavor3,
        getBrand().theme.palette.flavors.flavor7,
        getBrand().theme.palette.flavors.flavor8,
        getBrand().theme.palette.flavors.flavor6,
        getBrand().theme.palette.flavors.flavor4,
    ];

    if (!hash) {
        console.warn('Missing hash in utils method');
        return colors[0];
    }

    return colors[Math.abs(parseInt(hash)) % colors.length];
};

// Asks for the resource itself rather than for the key: both reducer paths
// write the key through even when its value is empty, so `has` would build an
// endpoint URL ending in "null" and the request would fail.
export const getContactAvatar = (
    contact,
    defaultAvatar = DEFAULT_AVATAR_SVG,
) => {
    const avatarResource = contact?.get('avatarResource');
    if (!avatarResource) {
        return defaultAvatar;
    }
    return HomeClient.getActiveEndpoint() + avatarResource;
};

export const getFlowExtensionContactAvatar = (
    contact,
    defaultAvatar = UNKNOWN_AVATAR_SVG,
) => {
    const avatarResource = contact?.get('avatarResource');
    if (!avatarResource) {
        return defaultAvatar;
    }
    return HomeClient.getActiveEndpoint() + avatarResource;
};

export const getChatRoomAvatar = (src) => {
    return HomeClient.getActiveEndpoint() + src;
};
export const sortByName = (extension0, extension1) => {
    const name0 = extractName(extension0.get('contact'), '');
    const name1 = extractName(extension1.get('contact'), '');

    return sortStrings(name0, name1);
};

export const getAttachmentPreviewURL = (attachment) => {
    if (attachment.get('previewUrl')) {
        return attachment.get('previewUrl');
    }
    return 'data:image/jpg;base64,' + attachment.get('previewData');
};

export const getAttachmentLink = (chatSessionEntityKey, attachment) => {
    return (
        HomeClient.getActiveEndpoint() +
        '/chats/' +
        chatSessionEntityKey +
        '/attachment/' +
        attachment.get('uuid')
    );
};

/**
 * Get the mobile data usage graphs from home
 *
 * @param extensionKey
 * @param interval should be month or year
 * @returns {string}
 */
export const getUsageGraphLink = (extensionKey, interval) => {
    return (
        HomeClient.getActiveEndpoint() +
        '/extensions/' +
        extensionKey +
        '/mobile/chart/' +
        interval +
        '?height=300'
    );
};

export const ISO_8601_MS_RESOLUTION = 'YYYY-MM-DDTHH:mm:ss.SSSZZ';

export const ISO_8601 = 'YYYY-MM-DDTHH:mm:ss';

/**
 * Locate an ExtensionDTO with the given topRouteKey
 * @param extensions List of extensions.
 * @param topRouteKey Key that Top uses for routing
 * @returns {null}
 */
export const findExtensionByTopRouteKey = (extensions, topRouteKey) => {
    const found = extensions
        .get('extensions')
        .filter((e) => e.get('topRouteKey') === topRouteKey);

    if (found && found.size > 0) {
        return found.toList().get(0);
    } else {
        return null;
    }
};

/**
 * Check if the supplied key is an ExtensionEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ExtensionEntityKey format
 */
export const isExtensionKey = (key) => /^extension-[0-9]+$/.test(key);

/**
 * Check if the supplied key is a queueKey, referKey or an extensionKey.
 * @param key the entity key
 * @return true if the supplied key is a queueKey, referKey or an extensionKey.
 */
export const isEntityKey = (key) =>
    isExtensionKey(key) ||
    isQueueKey(key) ||
    isReferKey(key) ||
    isChannelKey(key) ||
    isConferenceKey(key) ||
    isConnectionKey(key) ||
    isVoicemailKey(key);

/**
 * Check if the supplied key is a ChatUserEntitykey.
 *
 * @param key the entity key
 * @return true if the supplied key matches the ChatUserEntityKey format
 */
export const isChatUserKey = (key) => /^chatuser-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ProfileEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ProfileEntityKey format
 */
export const isProfileKey = (key) => /^profile-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an QueueEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches QueueEntityKey format
 */
export const isQueueKey = (key) => /^queue-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an VoicemailEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches QueueEntityKey format
 */
export const isVoicemailKey = (key) => /^voicemail-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ReferEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ReferEntityKey format
 */
export const isReferKey = (key) => /^refer-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ConferenceEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ConferenceEntityKey format
 */
export const isConferenceKey = (key) => /^userconference-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ConferenceMemberEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ConferenceMemberEntityKey format
 */
export const isConferenceMemberKey = (key) =>
    /^userconferencemember-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ContactEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ContactEntityKey format
 */
export const isContactKey = (key) => /^contact-[0-9]+$/.test(key);

/**
 * Check if the supplied key is a ThirdPartyContactEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ThirdPartyContactEntityKey format
 */
export const isThirdPartyContactKey = (key) =>
    /^thirdpartycontact-[0-9-]+$/.test(key);

/**
 * Check if the supplied key is an ChatMessageEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ChatMessageEntityKey format
 */
export const isChatMessageKey = (key) => /^chatmessage-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ChatMessageEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ChatMessageEntityKey format
 */
export const isFailedChatMessageKey = (key) =>
    /^failedchatmessage-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an ChatSessionEntityKey.
 * @param key the entity key
 * @return true if the supplied key matches ChatSessionEntityKey format
 */
export const isChatSessionKey = (key) => /^chatsession-[0-9]+$/.test(key);

/**
 * Check if the supplied key is an UserSoundKey.
 * @param key the entity key
 * @return true if the supplied key matches UserSoundKey format
 */
export const isUserSoundKey = (key) => /^usersound--?[0-9]+$/.test(key);

/**
 * Check if the supplied key is a SharedExtensionKey.
 * @param key the entity key
 * @return true if the supplied key matches SharedExtensionKey format
 */
export const isConnectionKey = (key) => /^shared-[0-9]+$/.test(key);

/**
 * Check if the supplied key is a ChannelKey.
 * @param key the entity key
 * @return true if the supplied key matches ChannelKey format
 */
export const isChannelKey = (key) => /^channel-[0-9]+$/.test(key);

export const isOperatorKey = (operatorKey) =>
    /^operator-[0-9]+$/.test(operatorKey);

export const isIntegrationDomainKey = (key) =>
    /^integrationaccessdomain-[0-9]+$/.test(key);

export const isIntegrationAccessKey = (key) =>
    /^integrationaccess-[0-9]+$/.test(key);
/**
 * Check if the supplied key is a BlockListKey.
 * @param {*} key the block list key
 * @return true if the supplied key matches BlockListKey format
 */
export const isBlockListKey = (key) => /^blocklist-[0-9]+?/.test(key);

export const isQueueCallKey = (key) => /^queuecall-+?/.test(key);

/**
 * Check if the supplied key is a ActiveCallKey.
 * @param key the active call key
 * @return true if the supplied key matches active call format
 */
export const isActiveCallKey = (key) => /^activecall-[0-9||.]+$/.test(key);

export const isTicketKey = (key) => /^ticket-+?/.test(key);

export const validEmailRegex = (email) => {
    return /[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?/.test(
        email,
    );
};

// Convert list to map with keys extracted from item by 'keyFunc' function.
export const listToMap = (list, keyFunc) =>
    list.reduce((acc, i) => acc.set(keyFunc(i), i), Map());

// Group items in a list by the specified grouper function
export const groupListBy = (list, grouper) =>
    list.reduce(
        (acc, item) =>
            acc.update(grouper(item), List(), (prev) => prev.push(item)),
        Map(),
    );

/**
 * Generates path to a personal voicemail message
 * @param extensionKey from the users extension
 * @param uniqueId from the specific message
 * @returns {string}
 */
export const voiceMessageUrlPath = (extensionKey, uniqueId) => {
    return (
        HomeClient.getActiveEndpoint() +
        '/extensions/' +
        extensionKey +
        '/voiceMessages/' +
        uniqueId
    );
};

/**
 * Generates path to a shared voicemail message
 * @param extensionKey from the shared voicemail box
 * @param uniqueId from the specific message
 * @returns {string}
 */
export const sharedVoiceMessageUrlPath = (extensionKey, uniqueId) => {
    return (
        HomeClient.getActiveEndpoint() +
        '/voicemails/' +
        extensionKey +
        '/voiceMessages/' +
        uniqueId
    );
};

/**
 * Extract the operator note from the profile. Defaults to an empty string '';
 */
export const extractOperatorNoteText = (profile = Map()) => {
    return profile.get('operatorNote');
};

export const getProfileDescriptionAndOperatorNote = (profile) => {
    if (!profile || profile.isEmpty()) {
        return '';
    }

    const operatorNote = extractOperatorNoteText(profile);
    const description = profile.get('description');

    if (!operatorNote) return description;

    return `${description} - ${operatorNote}`;
};

export const extractStatusFromProfile = (profile, i18next) => {
    if (!profile || profile.isEmpty()) {
        return '';
    }

    const operatorNote = extractOperatorNoteText(profile);
    const statusText = extractStatusText(profile, i18next);

    if (operatorNote) {
        return (
            statusText +
            ' - ' +
            operatorNote +
            ' ' +
            getActiveUntil(profile, i18next)
        );
    } else {
        return statusText + ' ' + getActiveUntil(profile, i18next);
    }
};

/**
 * Extract the description of the extension's status from its profile.
 * If the description is a standard profile description, we make sure to return it in the logged in user's i18 language.
 * The reason for the massaging being done by getStandardProfileDescriptionInUserLanguage is that some profile descriptions (that were created a long time ago?)
 * are not necessarily returned in the correct language.
 *
 * Defaults to 'available' (in the logged in user's i18 language).
 */
export const extractStatusText = (profile, i18next) => {
    if (profile) {
        const profileDescription = profile.get('description');
        if (profileDescription) {
            return getStandardProfileDescriptionInUserLanguage(
                profileDescription,
                i18next,
            );
        }
    }
    return i18next.t('status.available');
};

export const getProfileAvailabilityColorByStatus = (status) => {
    return extensionStatuses[status ? status : 'unknown'].getColor();
};

export const getActiveUntil = (profile, i18next) => {
    if (typeof profile === 'undefined') {
        return '';
    }
    const activeUntil = profile.get('activeUntil');
    const duration =
        i18next.t('profilechooser.until') +
        ' ' +
        getProfileLength(activeUntil, i18next);

    return activeUntil ? duration : '';
};

export const getProfileLength = (date, i18next) => {
    if (date === undefined) {
        return '';
    }
    const now = moment().startOf('day');
    const thatDate = moment(date).startOf('day');
    const daysDiff = Math.abs(now.diff(thatDate, 'days'));

    if (daysDiff <= 0) {
        return moment(date).format('HH:mm');
    } else if (daysDiff <= 7) {
        return (
            thatDate.locale(i18next.language).format('dddd') +
            ' ' +
            getEndTimeOfActiveProfile(date)
        );
    } else if (daysDiff <= 365) {
        return (
            moment(date).format('D') +
            ' ' +
            thatDate.locale(i18next.language).format('MMMM')
        );
    } else {
        return moment(date).format('YYYY-MM-DD HH:mm');
    }
};

const getEndTimeOfActiveProfile = (date) => {
    const time = moment(date).format('HH:mm');

    return time === '00:00' ? '' : time;
};

export const homeActionUrlPath = (action) => {
    if (appConfig.production) {
        return HomeClient.apiHost + action;
    }
    return action;
};

/**
 * Comparator function to sort ChatSessionDTOs based on sent date for the latestMessage
 *
 * @param a chatsessionDTO
 * @param b chatsessionDTO
 * @returns {number}
 */
export const newestFirst = (a, b) => {
    const msg_a = a.get('latestMessage');
    const msg_b = b.get('latestMessage');

    if (msg_a && msg_b) {
        const sent_a = msg_a.get('sent');
        const sent_b = msg_b.get('sent');

        return sent_b.localeCompare(sent_a); // Attempting to avoid date parsing
    } else if (msg_a) {
        return -1;
    } else if (msg_b) {
        return 1;
    } else {
        return 0;
    }
};

export const isEmptyNullOrUndefined = (input) => {
    return input === undefined || input === null || input === '';
};

const countNonMutedSessions = (count, session) => {
    if (session.get('chatMuted', false)) {
        return count;
    }
    return count + session.get('numberOfUnreadMessages', 0);
};

/**
 * Derive the sum of unread messages.
 *
 *
 * @param List[ChatSessionDTO]
 * @return the total number of unread messages or 0.
 */
export const calcTotalUnreadMessages = (sessions) => {
    return sessions.reduce(
        (acc, session) => countNonMutedSessions(acc, session),
        0,
    );
};

/**
 * Get a random string.
 */
export const generateRandomString = () => {
    return Math.random().toString(36).slice(2);
};

/**
 * Function to retrieve the id from a entityKey.
 *
 * @param entityKey
 * @returns {*}
 */
export const getIdFromEntityKey = (entityKey) => {
    return entityKey.split('-')[1];
};

/**
 * Attempt to locate a ContactDTO given chatSessionKey and chatUserKey.
 *
 * 1. First we attempt to locate the contact from the chat session members list,
 *    and if that fails,
 * 2. look for the contact in the list of messages if a previous message has
 *    been received from the sought author
 * 3. look for it in the list of extensions, provided the list has been
 *    retrieved in the first place.
 *
 * There are two ways to improve any bugs resulting from this function:
 * 1. Add contact information in push-notice coming from server side
 * 2. Fetch the list of extensions when preparing the initial application state
 */
export const findContactFromChatUserKey = (
    getState,
    chatSessionKey,
    chatUserKey,
) => {
    const { chats } = getState();

    // Find contact from list of members in session
    const contactFromMembers = chats
        .get('sessions')
        .find((session) => session.get('key') === chatSessionKey, null, Map())
        .get('members', List())
        .find((member) => member.get('key') === chatUserKey, null, Map())
        .get('contact');

    if (contactFromMembers) {
        return contactFromMembers;
    }

    // Find earlier message from same author, with a (hopefully) set contact
    const contactFromAuthor = chats
        .getIn(['messages', chatSessionKey], List())
        .find((m) => m.getIn(['from', 'key']) === chatUserKey, null, Map())
        .getIn(['from', 'contact']);

    if (contactFromAuthor) {
        return contactFromAuthor;
    }

    // Find the contactDTO in list of extensions, by matching chatUserKey
    const contactFromExtension = findExtension(
        getState,
        (e) => e.get('chatUserKey') === chatUserKey,
    ).get('contact');

    if (contactFromExtension) {
        return contactFromExtension;
    }

    return null;
};

export const keyIn = (...keys) => {
    const keySet = Set(keys);
    return function (_value, key) {
        return keySet.has(key);
    };
};

export const getScreenPosition = (left, top) => {
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;

    if (top < windowHeight / 2) {
        if (left < windowWidth / 3) {
            return ScreenPositions.TOP_LEFT;
        } else if (left > (windowWidth / 3) * 2) {
            return ScreenPositions.TOP_RIGHT;
        } else {
            return ScreenPositions.TOP_MIDDLE;
        }
    } else {
        if (left < windowWidth / 2) {
            return ScreenPositions.BOTTOM_LEFT;
        } else {
            return ScreenPositions.BOTTOM_RIGHT;
        }
    }
};

/**
 * This is the number that we call to play the usersoundid that is suffixed to the number.
 *
 * See UserSoundDAO for more information about usage.
 */
export const SOUND_PLAYER_ANUMBER = '0046913582599';

/**
 * Merge two lists containing objects along a key, instead of along their index.
 *
 * The objects in the lists need to be 'keyed', i.e. they need to have a
 * property called 'key'. If this key matches between two objects, the second
 * object is merged deeply into the first object

 * @param firstList     Immutable List with keyed objects
 * @param secondList    Immutable List with keyed objects
 * @param keepKeys      Keep keys from the first list, that are
 *                      not present in the second list
 * @return {*} An Immutable List containing merged objects. Might be empty.
 */
export const mergeKeyedLists = (firstList, secondList, keepKeys = true) => {
    const getKey = (object) => object.get('key');
    if (!List.isList(firstList)) {
        throw new Error(
            'First parameter needs to be a non-falsy Immutable list',
        );
    }
    if (!List.isList(secondList)) {
        throw new Error(
            'Second parameter needs to be a non-falsy Immutable list',
        );
    }
    let firstMap = listToMap(firstList, getKey);
    const secondMap = listToMap(secondList, getKey);

    if (!keepKeys) {
        firstMap = firstMap.filter(keyIn(...secondMap.keys()));
    }

    return firstMap.mergeDeep(secondMap).toList();
};

/**
 *
 * @param durationInMilliseconds number in milliseconds. Should be a positive value.
 * @returns string formated in:
 *                             h:mm:ss if durationInMilliseconds is equal to or greater than one hour
 *                             mm:ss if durationInMilliseconds is less than one hour.
 *                             If durationInMilliseconds is less than zero or not a number, an empty
 *                             string is returned.
 *
 */
export const getDurationInUserFriendlyFormat = (durationInMilliseconds) => {
    if (
        durationInMilliseconds < 0 ||
        typeof durationInMilliseconds !== 'number' ||
        Number.isNaN(durationInMilliseconds)
    ) {
        return '';
    }

    const seconds = Math.floor((durationInMilliseconds / 1000) % 60);
    const minutes = Math.floor((durationInMilliseconds / (1000 * 60)) % 60);
    const hours = Math.floor((durationInMilliseconds / (1000 * 3600)) % 3600);

    let secondsFriendlyFormatted = seconds;
    let minutesFriendlyFormatted = minutes;

    if (seconds < 10) {
        secondsFriendlyFormatted = '0' + seconds;
    }

    if (minutes < 10) {
        minutesFriendlyFormatted = '0' + minutes;
    }

    if (hours < 1) {
        return minutesFriendlyFormatted + ':' + secondsFriendlyFormatted;
    } else {
        return (
            hours +
            ':' +
            minutesFriendlyFormatted +
            ':' +
            secondsFriendlyFormatted
        );
    }
};

export const valueIsTruthyOrZero = (value) => {
    return !!(value || value === 0);
};

export const replaceNonDigitsWithBlank = (value) => {
    if (!isNaN(value)) {
        return value;
    }

    return value ? value.replace(/[^0-9]/g, '') : '';
};

export const valueEditedToNotExceedHundredOrGoBelowZero = (value) => {
    if (isNaN(value)) {
        return value;
    }

    if (value > 100) {
        return 100;
    }

    if (value < 0) {
        return 0;
    }

    return Number(value);
};

export const getItemsExistingInArray1ButNotInArray2 = (array1, array2) => {
    if (!array1 || !Array.isArray(array1)) {
        return [];
    }

    if (!array2 || !Array.isArray(array2)) {
        return array1;
    }
    return array1.filter((item) => !array2.includes(item));
};

export const getKeysExistingInMap1ButNotInMap2 = (map1, map2) => {
    if (!map1) {
        return [];
    }

    const [...keys] = map1.keys();

    if (!map2) {
        return keys;
    }

    const [...keysToExclude] = map2.keys();

    return getItemsExistingInArray1ButNotInArray2(keys, keysToExclude);
};

export const prettyPersonalNumber = (personalNumber) => {
    if (!personalNumber) {
        return undefined;
    }

    const dashPosition = personalNumber.length - 4;
    return [
        personalNumber.slice(0, dashPosition),
        '-',
        personalNumber.slice(dashPosition),
    ].join('');
};

export const stringStartsWithHttps = (urlString) => {
    if (!urlString) {
        return false;
    }

    return urlString.startsWith('https://');
};

export const addHttpsIfNoProtocol = (url) => {
    if (!url || typeof url !== 'string') {
        return null;
    }

    const hasProtocol = url.startsWith('http://') || url.startsWith('https://');
    return hasProtocol ? url : `https://${url}`;
};

/**
 * Convert to unicode.
 */
export const atou = (string) => decodeURIComponent(escape(atob(string)));

/**
 * 32 bit integer hash from string. Javas String.hashCode() implementation in JavaScript,
 * taken from https://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
 */
export const hashCodeFromString = (string) => {
    let hash = 0,
        i,
        chr;
    if (string.length === 0) return hash;
    for (i = 0; i < string.length; i++) {
        chr = string.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
};

/**
 * Tries to parse a String to an Object.
 * @return an Object, or if the argument is parsed to anything other than an Object
 * or fails, it will return the argument unchanged.
 */
export const tryParseJsonObject = (text) => {
    if (typeof text !== 'string') {
        return text;
    }
    try {
        const obj = JSON.parse(text);
        if (typeof obj === 'object') {
            return obj;
        }
    } catch (error) {
        addUserPhoneLogEntry(
            `Could not parse ${text} to JSON object: ${error}. Returning as string instead.`,
        );
    }
    return text;
};

export const getTagsFromString = (string) => {
    if (typeof string !== 'string') return [];

    const matches = string.split(/[\s,#]+/);

    return matches.filter((match) => match.length > 0);
};

export const getStringWithFirstLetterFromSecondWord = (phrase) => {
    if (typeof phrase !== 'string') return phrase;

    const phraseSplit = phrase.split(' ');
    if (phraseSplit.length > 1) {
        return `${phraseSplit[0]} ${phraseSplit[1].charAt(0)}`;
    } else {
        return phrase;
    }
};
